import {
    connect,
    injectReducer,
    injectSaga,
} from '@vfde-sails/core';
import saga from './saga';
import reducer from './reducer';
import * as appActions from './action';
import {
    State,
    mapStateToProps,
} from './selector';
import {
    mountLoadingSpinner,
    setLoadingSpinnerVisibility,
} from '../../components/loadingSpinner';
import {
    AppId,
    AvcFormId,
    CheckedAddressId,
    ContentId,
    CustomerInfoId,
    OfferSummaryCardId,
    DiscountId,
    Kip1000CableMax,
} from '../../enums/elementId';
import isCmsConfigValid from '../../util/cmsValidator';
import { ErrorType } from './enum';
import { debugInfo } from '../../util/debug';
import {
    Cookies,
    Namespace,
} from '../../enum';
import {
    loadInitialAppState,
    saveStateToSession,
} from './util/session.util';
import overlayContainer from '../Overlay/index';
import tariffSectionContainer from '../TariffSelection/index';
import discountSectionContainer from '../DiscountSection/index';
import { getUpdateFlags } from './update';
import { setElementVisibility } from '../../util/visibility';
import {
    mountOfferSummaryCard,
    updateOfferSummaryCard,
    updatePriceElements,
} from '../../components/offerSummaryCard';
import { setOverlayVisibility } from '../Overlay/action';
import { updateCableConnectionModule } from '../../components/offerSummaryCard';
import { OverlayContent } from '../Overlay/enum';
import { mountAccordion } from '../../components/accordion';
import { mountRelatedLinks } from '../../components/relatedLinks';
import {
    mountJustText,
    updateCustomerInfo,
} from '../../components/justText';
import { toAddressString } from '@vfde-sails/avc-core';
import {
    getSessionStorageItemJson,
    setSessionStorageItemJson,
} from '@vfde-sails/storage';
import {
    PS_COOKIE_VALUE,
    SCROLL_POSITION_KEY,
} from '../../constants';
import {
    mountButton,
    updateFloatingMobileButton,
} from '../../components/button';
import { mountIconTextBanner } from '../../components/iconTextBanner';
import {
    mountPromotionalCard,
    updatePsPromotionCard,
} from '../../components/promotionalCard';
import {
    addAVCOpenActionCashbackCard,
    registerPublicevent,
    setupScrollToTarget,
    updateCashbackCard,
    updatePromoVisibility,
} from '../../util/components/promotionalCard.util';
import {
    mountFootnoteLink,
    updateFnfFootnoteLink,
} from '../../components/footnoteLink';
import { isPlaystationPromoActive } from '../../util/cms';
import {
    mountImageHeader,
    updateImageHeader,
} from '../../components/imageHeader';
import { setCookie } from '@vfde-sails/utils';

const mapDispatch = {
    ...appActions,
    setOverlayVisibility,
};

type Actions = typeof mapDispatch;

const restoreScrollPosition = () => {
    const scrollPosition = getSessionStorageItemJson<number>(SCROLL_POSITION_KEY);

    if (scrollPosition) {
        window.scrollTo(0, scrollPosition);
        setSessionStorageItemJson(SCROLL_POSITION_KEY, null);
    }
};

const mountApp = (actions: Actions) => {
    if (!isCmsConfigValid()) {
        actions.setError({
            type: ErrorType.CMS_CONFIG,
            error: Error('Invalid CMS config!'),
        });

        debugInfo('CMS Config is invalid!');

        return { loadingSpinner: null, offerSummaryCard: null };
    }

    const offerSummaryCard = mountOfferSummaryCard(OfferSummaryCardId.OFFER_SUMMARY_CARD_WRAPPER);
    const offerSummaryCardCableConnect = mountOfferSummaryCard(OfferSummaryCardId.CABLE_CONNECTION_MODULE);
    const loadingSpinner = mountLoadingSpinner(AppId.LOADING);
    const addressText = mountJustText(CheckedAddressId.ADDRESS_TEXT);
    const floatingBtn = mountButton(AppId.FLOATING_BUTTON);

    mountRelatedLinks(AppId.RELATED_LINKS);
    mountRelatedLinks(AppId.RELATED_LINKS_OFFERS);
    mountAccordion(ContentId.FAQ_ACCORDION);
    mountIconTextBanner(AppId.ICON_TEXT_BANNER);

    return { loadingSpinner, offerSummaryCard, offerSummaryCardCableConnect, addressText, floatingBtn };
};
// appContainer

function appContainer (state: State, actions: Actions) {
    injectSaga(Namespace.APP, saga);
    injectReducer(Namespace.APP, reducer);

    const { loadingSpinner, offerSummaryCard, offerSummaryCardCableConnect, addressText, floatingBtn } = mountApp(actions);

    restoreScrollPosition();

    overlayContainer();
    tariffSectionContainer();
    discountSectionContainer();

    actions.initStore(loadInitialAppState());

    const openAvailabilityCheck = () => actions.setOverlayVisibility(true, OverlayContent.AVC);
    const openLegalForm = () => actions.setOverlayVisibility(true, OverlayContent.LEGAL);
    const cashbackPromotionCard = mountPromotionalCard(DiscountId.CASH_BACK);
    const playstationPromoCard = mountPromotionalCard(DiscountId.PLAYSTATION_PROMO_CARD);
    const imageHeader = mountImageHeader(AppId.IMAGE_HEADER);

    addAVCOpenActionCashbackCard(openAvailabilityCheck);
    /**
     * register a public event to be used by tealium side so that after closing the overly form of tealium then we can
     * use the promotional brix component to update the state of the checkbox and text
     */
    cashbackPromotionCard && registerPublicevent(cashbackPromotionCard);

    return {
        getDerivedStateFromProps (newState: State, oldState: State) {
            const {
                selectedTariff,
                marketability,
                isLoading,
                isContentEnabled,
                isFriendsAndFamily,
                isYoung,
                isAvailabilityChecked,
                isTVProductSelected,
                selectedTVProduct,
                address,
                customer,
                isOverlayOpen,
                selectedTariffId,
                isLoggedInCableCustomer,
            } = newState;
            const update = getUpdateFlags(newState, oldState);

            update.loading && setLoadingSpinnerVisibility(loadingSpinner, isLoading && !isOverlayOpen);
            update.isAvcError && setElementVisibility(AvcFormId.SERVICE_ERROR);

            if (update.isInitError) {
                setElementVisibility(AppId.ERROR);
                setElementVisibility(AppId.APP, false);
            }

            const btnProps = offerSummaryCard?.getProperties();
            updateFloatingMobileButton(
                floatingBtn,
                btnProps,
                selectedTariff,
                isTVProductSelected && selectedTVProduct ? openLegalForm : openAvailabilityCheck, newState);

            if (update.offerSummaryCard) {
                updateOfferSummaryCard(
                    offerSummaryCard,
                    newState,
                    isTVProductSelected && selectedTVProduct ? openLegalForm : openAvailabilityCheck,
                );
            }

            if (update.friendsAndFamily || update.young || update.tvProduct) {
                updatePriceElements(
                    offerSummaryCard,
                    selectedTariff,
                    selectedTVProduct,
                    isContentEnabled,
                    isFriendsAndFamily,
                    isYoung,
                    isTVProductSelected,
                );
            }

            if (update.avc) {
                setElementVisibility(CheckedAddressId.ADDRESS_WRAPPER, isAvailabilityChecked && !customer);
                setElementVisibility(CustomerInfoId.WRAPPER, !!customer);

                address && addressText?.update({ content: toAddressString(address) });
                customer && address && updateCustomerInfo(customer, address);

                updateCableConnectionModule(
                    offerSummaryCardCableConnect,
                    isAvailabilityChecked,
                    Boolean(marketability?.isCableConnection),
                );
            }

            const isPlaystationPromo = !!(selectedTariffId && !isLoggedInCableCustomer &&
                Object.values(Kip1000CableMax).includes(selectedTariffId as Kip1000CableMax) &&
                isPlaystationPromoActive());

            if (isPlaystationPromoActive()) {
                setCookie(Cookies.PLAYSTATION_PROMO, { value: PS_COOKIE_VALUE });
            }

            if (isPlaystationPromo && !isFriendsAndFamily && !isYoung) {
                updatePsPromotionCard(playstationPromoCard);
                setupScrollToTarget(DiscountId.PS_LINK, DiscountId.PS_BANNER);
                updatePromoVisibility(isPlaystationPromo);
            }

            updatePromoVisibility(isPlaystationPromo);
            updateImageHeader(imageHeader, isYoung, isPlaystationPromo);
            updateCashbackCard(newState, cashbackPromotionCard, openAvailabilityCheck, isPlaystationPromo);
            mountFootnoteLink(DiscountId.CASH_BACK);
            mountFootnoteLink(DiscountId.PLAYSTATION_PROMO_CARD);

            !state.isContentEnabled && mountFootnoteLink(DiscountId.CASH_BACK_DISABLED);

            setElementVisibility(ContentId.FOOTNOTE_PS_ACCORDION, isPlaystationPromo);
            setElementVisibility(ContentId.FOOTNOTE_FNF_ACCORDION, isFriendsAndFamily);

            isFriendsAndFamily && updateFnfFootnoteLink(isTVProductSelected);

            saveStateToSession(newState);
        },
    };
}

export default connect(mapStateToProps, mapDispatch)(appContainer);
